import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Switch,
  FormControlLabel,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';

import { CreateTaskEventDto, UpdateTaskEventDto, TaskDetails } from '../../../services/task.service';
import { CalendarEventDto } from '../../../types/calendar';
import { ModalIndices } from '../../../common/i-doc/i-doc.registry.ts';
import { toLocalIso } from '@/utils/datetime';
import { SchedulingBlock } from './SchedulingBlock';
import { eventColors } from '@/styles/tokens';

interface AddTaskEventModalV2Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateTaskEventDto | UpdateTaskEventDto) => Promise<void>;
  initialScheduledAt?: Date;
  eventToEdit?: CalendarEventDto | null;
}

const ACCENT = eventColors.task.main;
const ACCENT_HOVER = eventColors.task.dark;

export const AddTaskEventModalV2: React.FC<AddTaskEventModalV2Props> = ({
  open,
  onClose,
  onSave,
  initialScheduledAt,
  eventToEdit,
}) => {
  const [expertMode, setExpertMode] = useState(() => localStorage.getItem('task-modal-expert-mode') === 'true');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (eventToEdit) {
        setTitle(eventToEdit.title);
        const details = eventToEdit.details as TaskDetails;
        setNotes(details?.notes || '');
        setPriority(details?.priority || 'medium');
        setScheduledAt(eventToEdit.scheduledLdt ? new Date(eventToEdit.scheduledLdt) : new Date());
        setIsCompleted(eventToEdit.isCompleted);
        setShowDateTimePicker(true);
      } else {
        setTitle('');
        setNotes('');
        setPriority('medium');
        setScheduledAt(initialScheduledAt ?? null);
        setIsCompleted(false);
        setShowDateTimePicker(false);
      }
    }
  }, [open, eventToEdit]);

  const handleClose = () => {
    setTitle('');
    setNotes('');
    setPriority('medium');
    setScheduledAt(null);
    setShowDateTimePicker(false);
    setIsCompleted(false);
    onClose();
  };

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const finalDate = showDateTimePicker && scheduledAt ? scheduledAt : new Date();
      await onSave({
        title: title.trim(),
        details: { notes, priority },
        scheduledLdt: toLocalIso(finalDate),
        isCompleted,
      });
    } finally {
      setSaving(false);
    }
  };

  // ─── Header (shared) ──────────────────────────────────────────────────────
  const header = (
    <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssignmentIcon sx={{ color: ACCENT }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {eventToEdit ? 'Редагувати задачу' : 'Додати задачу'}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <FormControlLabel
          control={
            <Switch
              checked={expertMode}
              onChange={(e) => {
                const val = e.target.checked;
                setExpertMode(val);
                localStorage.setItem('task-modal-expert-mode', String(val));
              }}
              size="small"
              sx={{
                '& .MuiSwitch-thumb': { bgcolor: expertMode ? ACCENT : undefined },
                '& .MuiSwitch-track': { bgcolor: expertMode ? `${ACCENT} !important` : undefined },
              }}
            />
          }
          label={
            <Typography variant="caption" sx={{ fontWeight: 600, color: expertMode ? ACCENT : 'text.secondary' }}>
              Режим експерта
            </Typography>
          }
          labelPlacement="start"
          sx={{ mr: 0, ml: 0 }}
        />
        <IconButton onClick={handleClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </Box>
    </DialogTitle>
  );

  const schedulingBlock = (
    <SchedulingBlock
      showDateTimePicker={showDateTimePicker}
      onShowDateTimePickerChange={setShowDateTimePicker}
      scheduledAt={scheduledAt}
      onScheduledAtChange={setScheduledAt}
      isCompleted={isCompleted}
      onIsCompletedChange={setIsCompleted}
      switchLabel={expertMode ? 'Вказати час виконання' : 'Встановити дату та час'}
      switchSize="small"
    />
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  if (expertMode) {
    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '16px' } }} data-i-doc={ModalIndices.ADD_TASK_EVENT}>
        {header}
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1, bgcolor: 'action.hover', pt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label="Назва задачі"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={saving}
          />

          <TextField
            fullWidth
            label="Нотатки"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={saving}
          />

          <FormControl fullWidth disabled={saving}>
            <InputLabel>Пріоритет</InputLabel>
            <Select
              value={priority}
              onChange={(e) => setPriority(e.target.value as 'low' | 'medium' | 'high')}
              label="Пріоритет"
            >
              <MenuItem value="low">Низький</MenuItem>
              <MenuItem value="medium">Середній</MenuItem>
              <MenuItem value="high">Високий</MenuItem>
            </Select>
          </FormControl>

          {schedulingBlock}
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: 'action.hover' }}>
          <Button onClick={handleClose} disabled={saving}>Скасувати</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!title.trim() || saving || (showDateTimePicker && !scheduledAt)}
            sx={{ bgcolor: ACCENT, borderRadius: '10px', px: 3, '&:hover': { bgcolor: ACCENT_HOVER } }}
          >
            {saving ? <CircularProgress size={22} color="inherit" /> : (eventToEdit ? 'Зберегти' : 'Додати')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '16px' } }} data-i-doc={ModalIndices.ADD_TASK_EVENT}>
      {header}
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, bgcolor: 'action.hover', pt: 2 }}>
        <TextField
          autoFocus
          fullWidth
          label="Зміст задачі"
          multiline
          rows={3}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={saving}
          sx={{ bgcolor: 'white', '& .MuiInputBase-root': { borderRadius: '12px' } }}
        />

        {schedulingBlock}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: 'action.hover' }}>
        <Button onClick={handleClose} color="inherit" disabled={saving}>Скасувати</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!title.trim() || saving || (showDateTimePicker && !scheduledAt)}
          sx={{ bgcolor: ACCENT, borderRadius: '10px', px: 4, '&:hover': { bgcolor: ACCENT_HOVER } }}
        >
          {saving ? <CircularProgress size={22} color="inherit" /> : (eventToEdit ? 'Зберегти' : 'Додати')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
